import * as Location from 'expo-location';
import { useCallback } from 'react';

export type ShareLocationResult =
  /** A position was resolved; `url` opens it in the device's map app. */
  | { status: 'success'; url: string; latitude: number; longitude: number }
  /** The user declined (or has previously declined) the location permission. */
  | { status: 'denied' }
  /** Permission was granted but no fix could be obtained. */
  | { status: 'unavailable' };

/**
 * Maps link for a coordinate pair. `maps.google.com/?q=lat,lng` is the one form
 * every platform handles: iOS hands it to the Google Maps app when installed and
 * to Safari (which offers "Open in Maps") otherwise, Android opens it in Maps
 * directly, and desktop browsers resolve it to a pin. Six decimals is ~0.1 m,
 * well past the accuracy of any consumer GPS fix.
 */
export const buildMapsUrl = (latitude: number, longitude: number): string =>
  `https://maps.google.com/?q=${latitude.toFixed(6)},${longitude.toFixed(6)}`;

/**
 * One-shot "share my location": asks for foreground permission, takes a fresh
 * fix, and hands back a maps URL ready to drop into a message.
 *
 * Distinct from `useWatchLocation` (background asset tracking) and
 * `useCurrentLocation` (reads the last cached fix): this is user-initiated, so
 * it requests the permission up front rather than assuming a watcher already
 * populated storage.
 *
 * Deliberately does NOT fall back to the cached fix when the live lookup
 * fails. Someone sharing their location means "where I am now", and the cached
 * position can be hours stale, so a wrong pin is worse than an honest failure.
 * Avoiding `useCurrentLocation` also keeps this hook free of `GrippContext`,
 * so composers that use it still render outside the provider.
 */
export const useShareLocation = () => {
  const shareLocation = useCallback(async (): Promise<ShareLocationResult> => {
    const permission = await Location.requestForegroundPermissionsAsync();
    if (permission.status !== Location.PermissionStatus.GRANTED) {
      return { status: 'denied' };
    }

    try {
      const position = await Location.getCurrentPositionAsync({
        accuracy: Location.LocationAccuracy.Balanced,
      });
      const { latitude, longitude } = position.coords;
      return {
        status: 'success',
        url: buildMapsUrl(latitude, longitude),
        latitude,
        longitude,
      };
    } catch {
      // No live fix: indoors, airplane mode, or a simulator with no location
      // set. The caller surfaces this as "couldn't get your location".
      return { status: 'unavailable' };
    }
  }, []);

  return { shareLocation };
};
